import {BackendError, Group} from "@/lib/types";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/components/Page";
import {dataRetryPolicy, fetchBackend} from "@/lib/backend";
import ErrorPage from "@/lib/components/ErrorPage";
import {useQuery} from "@tanstack/react-query";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {useLocalSearchParams, useRouter} from "expo-router";
import Header from "@/lib/components/Header";
import ActionButton from "@/lib/components/ActionButton";
import {Platform, Pressable, StyleSheet, TextInput, View} from "react-native";
import Icon from "@/lib/components/Icon";
import {Color} from "@/lib/styles";
import globstyles from "@/lib/globstyles";
import {useEffect, useRef, useState} from "react";
import GroupCard from "@/lib/components/GroupCard";

const searchbarHeight = 42;

export default function Index() {
    const {session, isLoading} = useSession();
    const router = useRouter();
    const searchInput = useRef<TextInput>(null);

    const {group_id} = useLocalSearchParams();

    const [searchQuery, setSearchQuery] = useState("");

    const {data, isPending, error} = useQuery<Group[], BackendError>({
        queryKey: ['group', session],
        queryFn: () => fetchBackend("/group", "GET", session),
        retry: dataRetryPolicy,
        staleTime: 1000 * 60 * 15,
        enabled: !isLoading,
    })

    // redirect when landed on a opengraph metadata page.
    useEffect(() => {
        if (!group_id) {
            return;
        }
        router.replace(`/s`);
        router.push(`/s/${group_id}`);
    }, [group_id, router]);

    if (error && error.name === "ApiError") {
        return <ErrorPage error={error}></ErrorPage>;
    } else if (!!error) {
        const err = new Error("Klarer ikke hente samfunn akkurat nå.\n\nSjekk at du er tilkoblet Internett og prøv igjen.\n\nOriginal feilmelding: " + error.message);
        return <ErrorPage error={err}></ErrorPage>;
    }

    if (isPending) return <SpinnerPage/>;

    return (
        <Page scrollable={true}>
            <View style={styles.searchBar}>
                <Pressable focusable={false} onPress={() => router.navigate(`/s/search`)}>
                    <Icon kind={"logo"} size={searchbarHeight} color={Color.Yellow}/>
                </Pressable>
                <TextInput style={[globstyles.formTextInput, styles.searchField]}
                           placeholder={"Søk etter samfunn"}
                           placeholderTextColor={Color.FadedText}
                           value={searchQuery}
                           onChangeText={(query) => {
                               setSearchQuery(query);
                           }}
                           inputMode={"search"}
                           returnKeyType={"search"}
                           autoFocus={Platform.OS === "web"}
                           focusable={true}
                           keyboardAppearance={"dark"}
                           submitBehavior={"submit"}
                           onSubmitEditing={() => {
                               router.navigate(`/s/search?query=${searchQuery}`);
                               setSearchQuery("");
                               searchInput.current?.focus();
                           }}
                />
                {!!session &&
                    <ActionButton
                        icon={"plus"}
                        title={"Ny"}
                        height={searchbarHeight}
                        onPress={() => {
                            router.navigate(`/s/create`);
                        }}

                    />
                }
            </View>

            {(!data || data.length === 0) && (
                <Header title={"Ingen samfunn å vise."}/>
            )}

            {data.map(group => <GroupCard key={group.group_id} group={group}></GroupCard>)}
        </Page>
    );
}

export const styles = StyleSheet.create({
    searchBar: {
        flexDirection: "row",
        gap: 8,
        height: searchbarHeight,
        marginVertical: 4,
    },
    searchField: {
        width: "100%",
        flexShrink: 1,
    },
})